import { useState } from 'react'
import './StepsCard.css'

/**
 * StepsCard - Displays the investigation plan with steps.
 * 
 * Props:
 *   title: string
 *   subtitle: string | ReactNode
 *   steps: array of { text, detail, status, statusText, step_type }
 *   showProgress: { percent, label, error }
 *   showFooter: boolean - show add step + run button (plan state)
 *   onRun: function
 *   onEditStep: function(index, newText) - called when step text is edited
 *   onDeleteStep: function(index) - called when step is removed
 *   onAddStep: function
 */
export default function StepsCard({
  title = 'Investigation Plan',
  subtitle,
  steps = [],
  showProgress,
  showFooter = false,
  onRun,
  onEditStep,
  onDeleteStep,
  onAddStep,
}) {
  const [editingIndex, setEditingIndex] = useState(null)
  const [editText, setEditText] = useState('')

  const getStepNumberContent = (step, index) => {
    if (step.status === 'complete') return '✓'
    if (step.status === 'error') return '✕'
    return index + 1
  }

  const getStepNumberClass = (step) => `step-number ${step.status || ''}`

  const getStepItemClass = (step) => {
    let cls = 'step-item'
    if (step.status === 'running') cls += ' active-step'
    if (step.status === 'error') cls += ' error-step'
    if (step.status === 'paused') cls += ' paused-step'
    return cls
  }

  const handleStartEdit = (i) => {
    setEditingIndex(i)
    setEditText(steps[i].text)
  }

  const handleSaveEdit = (i) => {
    if (onEditStep && editText.trim()) {
      onEditStep(i, editText.trim())
    }
    setEditingIndex(null)
    setEditText('')
  }

  const handleCancelEdit = () => {
    setEditingIndex(null)
    setEditText('')
  }

  const handleKeyDown = (e, i) => {
    if (e.key === 'Enter') handleSaveEdit(i)
    if (e.key === 'Escape') handleCancelEdit()
  }

  // Steps in plan state (no status) are editable
  const isEditable = showFooter

  return (
    <div className="steps-card">
      <div className="steps-header">
        <h4>{title}</h4>
        <span className="steps-count">{subtitle}</span>
      </div>

      {steps.map((step, i) => (
        <div key={i} className={getStepItemClass(step)}>
          <div className={getStepNumberClass(step)}>
            {getStepNumberContent(step, i)}
          </div>
          <div className="step-content">
            {editingIndex === i ? (
              <div className="step-edit-wrap">
                <input
                  className="step-edit-input"
                  value={editText}
                  onChange={(e) => setEditText(e.target.value)}
                  onKeyDown={(e) => handleKeyDown(e, i)}
                  autoFocus
                />
                <div className="step-edit-actions">
                  <button className="step-edit-save" onClick={() => handleSaveEdit(i)}>Save</button>
                  <button className="step-edit-cancel" onClick={handleCancelEdit}>Cancel</button>
                </div>
              </div>
            ) : (
              <>
                <div className="step-text" style={step.status === 'pending' ? { color: 'var(--gray-400)' } : undefined}>
                  {step.text}
                </div>
                {step.stepTypeLabel && (
                  <span className={`step-type-badge step-type-${step.stepType || 'default'}`}>{step.stepTypeLabel}</span>
                )}
                {step.detail && <div className="step-detail">{step.detail}</div>}
                {step.statusText && <div className={`step-status ${step.status}`}>{step.statusText}</div>}
              </>
            )}
          </div>
          {isEditable && editingIndex !== i && (
            <div className="step-actions">
              <button className="step-action-btn" onClick={() => handleStartEdit(i)} title="Edit step">✎</button>
              <button className="step-action-btn" onClick={() => onDeleteStep?.(i)} title="Remove step">✕</button>
            </div>
          )}
        </div>
      ))}

      {showProgress && (
        <div className="progress-bar">
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{
                width: `${showProgress.percent}%`,
                background: showProgress.error ? 'var(--red)' : undefined,
              }}
            />
          </div>
          <span
            className="progress-label"
            style={showProgress.error ? { color: 'var(--red)' } : undefined}
          >
            {showProgress.label}
          </span>
        </div>
      )}

      {showFooter && (
        <div className="steps-footer">
          <button className="add-step-btn" onClick={onAddStep}>+ Add step</button>
          <button className="run-btn" onClick={onRun}>▶ Run Investigation</button>
        </div>
      )}
    </div>
  )
}
